import { useCallback, useEffect, useState } from 'react';
import { ApolloError } from 'apollo-boost';

import { Mode, Props } from './RetrieveBooking';
import { useConfig } from '../../context';
import { useClarifyDocumentModal } from './ClarifyDocumentModal/hooks';

const onlyAirportCheckInError = 'Invalid sub status: OnlyAirportCheckIn';

export function useRetrieveBooking({ mode, initialId, initialSecret, submitWithInitialValues, onSubmit }: Props) {
	const {
		global: {
			companyInfo: { iataCode }
		}
	} = useConfig();

	const clarifyDocumentModal = useClarifyDocumentModal();

	const [isLoading, setIsLoading] = useState(false);
	const [codeIsWrong, setCodeIsWrong] = useState(false);
	const [isBlockedForPayment, setIsBlockedForPayment] = useState(false);
	const [isOnlyAirportCheckin, setIsOnlyAirportCheckin] = useState(false);

	const isCheckoutMode = mode === Mode.Checkout;
	const isCheckinMode = mode === Mode.Checkin;

	const isErrorNotification = iataCode === 'A9' || iataCode === 'KV' || iataCode === 'F7';
	const isOrderNotFoundError = codeIsWrong && !isErrorNotification && !isOnlyAirportCheckin;

	useEffect(() => {
		if (submitWithInitialValues) {
			handleSubmit({ id: initialId, secret: initialSecret }, {});
		}
	}, [submitWithInitialValues]);

	const onSubmitForm = useCallback(
		(values: Record<string, any>) => {
			const id = values['id'];
			const secret = values['secret']?.trim();
			const extraSecret = values['extraSecret']?.trim();

			const payload = isCheckinMode ? { id, secret, extraSecret } : { id, secret, secret2fa: extraSecret };

			return onSubmit(payload);
		},
		[onSubmit, isCheckinMode]
	);

	const handleError = useCallback(
		(values: object, e: unknown) => {
			console.warn(e);

			if (
				e instanceof ApolloError &&
				e.graphQLErrors?.some(
					(error: any) =>
						error?.message?.includes(onlyAirportCheckInError) ||
						error?.debugMessage?.includes(onlyAirportCheckInError)
				)
			) {
				setIsOnlyAirportCheckin(true);
			} else if (e instanceof Error && clarifyDocumentModal.needToShowClarifyModal(e)) {
				clarifyDocumentModal.setSearchParams(values);
				clarifyDocumentModal.open();
			} else {
				setCodeIsWrong(true);
			}
		},
		[setIsOnlyAirportCheckin, setCodeIsWrong, clarifyDocumentModal]
	);

	const handleSubmit = useCallback(
		async (values: object, form) => {
			setCodeIsWrong(false);
			setIsLoading(true);
			setIsOnlyAirportCheckin(false);

			if (isCheckinMode && form.hasOwnProperty('getFieldState')) {
				// clear warning on submit
				form.getFieldState('secret').data.warning = null;
			}

			try {
				const result = await onSubmitForm(values);

				if (result) {
					if (typeof result !== 'boolean') {
						setIsBlockedForPayment(result.isBlockedForPayment);
					}
				} else {
					setCodeIsWrong(true);
				}
			} catch (e) {
				handleError(values, e);
			}

			setIsLoading(false);
		},
		[onSubmitForm, handleError, isCheckinMode]
	);

	const handleClarifySubmit = useCallback(
		async (values: object) => {
			try {
				const result = await onSubmitForm(values);

				return !!result;
			} catch (e) {
				console.warn(e);

				return false;
			}
		},
		[onSubmitForm]
	);

	return {
		// data
		isLoading,
		codeIsWrong,
		isCheckoutMode,
		isCheckinMode,
		isBlockedForPayment,
		isOnlyAirportCheckin,
		isErrorNotification,
		isOrderNotFoundError,
		isClarityDocumentOpen: clarifyDocumentModal.isOpen,
		searchParams: clarifyDocumentModal.searchParams,

		// methods
		handleSubmit,
		handleClarifySubmit,
		closeClarityDocument: clarifyDocumentModal.close
	};
}
